/* recommendationsService.js, single client for the Opportunity Engine.

   Every recommendation surface (partner card, contextual strip, dashboard
   ambient nudge) calls these helpers. Same engine, different presentation.

   Phase 1 (2026-05-29).
*/
import { trackGenerateLead } from '../lib/analytics';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

if (!API_BASE) {
  // Hard-fail loudly in dev so missing env is obvious.
  // eslint-disable-next-line no-console
  console.warn('[recommendations] REACT_APP_BACKEND_URL is not set');
}

/** Returns top-3 matches + coverage_state + fallback. */
export async function fetchRecommendations({
  category,
  state,
  buildingStage,
  projectType,
  budget,
  userId,
  sessionId,
  sourcePlacement,
  excludeIds = [],
  limit = 3,
}) {
  const r = await fetch(`${API_BASE}/api/opportunities/recommend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      category,
      state: state || null,
      building_stage: buildingStage || null,
      project_type: projectType || null,
      budget: budget || null,
      user_id: userId || null,
      session_id: sessionId || null,
      source_placement: sourcePlacement || null,
      exclude_business_ids: excludeIds,
      limit,
    }),
  });
  if (!r.ok) {
    return { results: [], coverage_state: 'disabled', fallback: {}, refinement_questions: [] };
  }
  return r.json();
}

/** Records a lead (introduction). Returns { lead_id, status, message }. */
export async function createLead({
  category,
  subcategory,
  businessId,
  businessName,
  partnerKey,
  sourcePlacement,
  sourcePage,
  state,
  suburb,
  buildingStage,
  refinementAnswers = {},
  contactName,
  contactEmail,
  contactPhone,
  userId,
  privacyConsent,
  leadSharingConsent,
}) {
  const r = await fetch(`${API_BASE}/api/leads/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      category,
      subcategory: subcategory || null,
      business_id: businessId || null,
      business_name: businessName || null,
      partner_key: partnerKey || null,
      source_placement: sourcePlacement,
      source_page: sourcePage || null,
      state: state || null,
      suburb: suburb || null,
      building_stage: buildingStage || null,
      refinement_answers: refinementAnswers,
      contact_name: contactName,
      contact_email: contactEmail,
      contact_phone: contactPhone || null,
      user_id: userId || null,
      privacy_consent: !!privacyConsent,
      lead_sharing_consent: !!leadSharingConsent,
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    return { status: 'error', message: data.detail || 'Something went wrong. Please try again.' };
  }
  // GA4 conversion (via GTM). Fire once per genuinely created lead, keyed on
  // the backend lead_id so retries of the same submission reuse one id. Skip
  // de-duplicated repeats. Non-PII only (never the email/name/phone).
  try {
    if (data && data.status !== 'duplicate' && data.status !== 'error') {
      trackGenerateLead({ category, state, acquisitionSource: sourcePlacement, dedupeId: data.lead_id });
    }
  } catch (e) { /* analytics never blocks lead creation */ }
  return data;
}

/** Fire-and-forget impression tracking. */
export function trackImpression({
  category,
  sourcePlacement,
  businessId,
  partnerKey,
  userId,
  sessionId,
  state,
  buildingStage,
  shownPosition,
  clicked = false,
}) {
  try {
    fetch(`${API_BASE}/api/opportunities/impression`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category,
        source_placement: sourcePlacement,
        business_id: businessId || null,
        partner_key: partnerKey || null,
        user_id: userId || null,
        session_id: sessionId || null,
        state: state || null,
        building_stage: buildingStage || null,
        shown_position: shownPosition || null,
        clicked,
      }),
    }).catch(() => {});
  } catch (_) {
    // intentionally silent, analytics must never block UX
  }
}

/** Tiny helper for anonymous session tracking. */
export function getOrCreateSessionId() {
  try {
    const KEY = 'bp_anon_session_id';
    let id = window.localStorage.getItem(KEY);
    if (!id) {
      id = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(KEY, id);
    }
    return id;
  } catch (_) {
    return null;
  }
}
